import { useState, useEffect } from 'react';
import { LocalizedLink as Link } from '../components/LocalizedLink';
import api from '../services/api';
import BlogLayout from '../components/BlogLayout';
import SEO from '../components/SEO';
import './BlogPage.css';
import { Calendar, User, BookOpen, ArrowRight } from 'lucide-react';
import { useTranslation } from "react-i18next";

import { getBlogImageProps } from '../utils/imageUtils';
import { SITE_URL } from '../config/site';

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  coverImageUrl: string | null;
  tags: string[] | null;
  authorName: string;
  publishedAt: string;
}

export default function BlogListPage() {
    const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState<BlogPost[]>(() => {
    if (typeof window !== 'undefined') {
      const el = document.getElementById('initial-blog-list');
      if (el && el.textContent) {
        try { return JSON.parse(el.textContent); } catch (e) {}
      }
    }
    return [];
  });
  const [isLoading, setIsLoading] = useState(() => {
    if (typeof window !== 'undefined') {
      return !document.getElementById('initial-blog-list');
    }
    return true;
  });
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    fetchPosts();
  }, [page, i18n.language]);

  const fetchPosts = async () => {
    setIsLoading(true);
    try {
      const res = await api.get('/blog/posts', { params: { page, size: 50, lang: i18n.language } });
      setPosts(res.data.content);
      setTotalPages(res.data.totalPages);
    } catch (err) {
      console.error('Failed to load blog posts');
    } finally {
      setIsLoading(false);
      if (typeof window !== 'undefined') {
        setTimeout(() => {
          (window as any).__PRERENDER_READY = true;
        }, 100);
      }
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString(i18n.language, {
      month: 'long', day: 'numeric', year: 'numeric',
    });



  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: posts.map((post, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `${SITE_URL}/blog/${post.slug}`,
      name: post.title
    }))
  };

  return (
    <BlogLayout>
      <SEO 
        title={t('seo.blogList.title')}
        description={t('seo.blogList.description')}
        schema={jsonLd}
      />
      
      {posts.length > 0 && <script type="application/json" id="initial-blog-list" suppressHydrationWarning dangerouslySetInnerHTML={{ __html: JSON.stringify(posts).replace(/</g, '\\u003c') }} />}

      <div className="blog-container animate-fade-in">
        {/* Hero */}
        <header className="blog-list-hero">
          <div className="blog-list-hero-label">{t('blog.list.reviioBlog')}</div>
          <h1>{t('blog.list.insightsForSmarter')}<br /><span className="blog-gradient-text">{t('blog.list.reviewManagement')}</span></h1>
          <p>{t('blog.list.tipsStrategiesAndGuidesOnGoogle')}</p>
          <div className="blog-category-filter-container">
            <Link to="/blog/category/response-and-automation" className="btn btn-secondary blog-category-pill">{t('common.responseAutomation')}</Link>
            <Link to="/blog/category/multi-platform-management" className="btn btn-secondary blog-category-pill">{t('blog.list.multiPlatform')}</Link>
            <Link to="/blog/category/insights-and-action" className="btn btn-secondary blog-category-pill">{t('common.insightsAction')}</Link>
          </div>
        </header>

        {isLoading ? (
          <div className="blog-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="blog-card-skeleton">
                <div className="blog-card-skeleton-cover" />
                <div className="blog-card-skeleton-body">
                  <div className="skeleton-line" style={{ width: '40%', height: 12 }} />
                  <div className="skeleton-line" style={{ width: '90%', height: 18 }} />
                  <div className="skeleton-line" style={{ width: '100%', height: 14 }} />
                  <div className="skeleton-line" style={{ width: '60%', height: 14 }} />
                </div>
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="blog-empty-state">
            <BookOpen size={56} />
            <h2>{t('blog.list.noArticlesYet')}</h2>
            <p>{t('blog.list.checkBackSoonForArticlesOn')}</p>
          </div>
        ) : (
          <>
            {/* Featured (first post) */}
            {posts.length > 0 && (
              <Link to={`/blog/${posts[0].slug}`} className="blog-featured-card">
                <div className="blog-featured-cover">
                  {posts[0].coverImageUrl ? (() => {
                    const imgProps = getBlogImageProps(posts[0].coverImageUrl);
                    return imgProps ? (
                      <picture>
                        {imgProps.srcSet && (
                          <source type="image/webp" srcSet={imgProps.srcSet} sizes="(max-width: 900px) 100vw, 600px" />
                        )}
                        <img src={imgProps.src} alt={posts[0].title} width={800} height={450} loading="lazy" />
                      </picture>
                    ) : (
                      <img src={posts[0].coverImageUrl} alt={posts[0].title} width={800} height={450} loading="lazy" />
                    );
                  })() : (
                    <div className="blog-featured-cover-placeholder">
                      <BookOpen size={64} />
                    </div>
                  )}
                </div>
                <div className="blog-featured-body">
                  {posts[0].tags && posts[0].tags.length > 0 && (
                    <div className="blog-card-tags">
                      {posts[0].tags.slice(0, 3).map(tag => (
                        <span key={tag} className="blog-card-tag">{tag}</span>
                      ))}
                    </div>
                  )}
                  <h2 className="blog-featured-title">{posts[0].title}</h2>
                  {posts[0].excerpt && <p className="blog-featured-excerpt">{posts[0].excerpt}</p>}
                  <div className="blog-featured-meta">
                    <span><User size={14} />{` ${posts[0].authorName}`}</span>
                    <span><Calendar size={14} />{` ${formatDate(posts[0].publishedAt)}`}</span>
                    <span className="blog-read-link">{t('blog.list.readArticle')}<ArrowRight size={14} /></span>
                  </div>
                </div>
              </Link>
            )}

            {/* Grid for remaining posts */}
            {posts.length > 1 && (
              <div className="blog-grid">
                {posts.slice(1).map((post, index) => {
                  const isWide = index === 0 || index === 3 || index === 7;
                  return (
                  <Link to={`/blog/${post.slug}`} key={post.id} className={`card blog-card ${isWide ? 'blog-card-wide' : ''}`}>
                    {post.coverImageUrl ? (() => {
                      const imgProps = getBlogImageProps(post.coverImageUrl);
                      return imgProps ? (
                        <picture>
                          {imgProps.srcSet && (
                            <source type="image/webp" srcSet={imgProps.srcSet} sizes="(max-width: 768px) 100vw, 400px" />
                          )}
                          <img src={imgProps.src} alt={post.title} className="blog-card-cover" width={800} height={450} loading="lazy" />
                        </picture>
                      ) : (
                        <img src={post.coverImageUrl} alt={post.title} className="blog-card-cover" width={800} height={450} loading="lazy" />
                      );
                    })() : (
                      <div className="blog-card-cover-placeholder">
                        <BookOpen size={40} />
                      </div>
                    )}
                    <div className="blog-card-body">
                      {post.tags && post.tags.length > 0 && (
                        <div className="blog-card-tags">
                          {post.tags.slice(0, 2).map(tag => (
                            <span key={tag} className="blog-card-tag">{tag}</span>
                          ))}
                        </div>
                      )}
                      <h2 className="blog-card-title">{post.title}</h2>
                      {post.excerpt && <p className="blog-card-excerpt">{post.excerpt}</p>}
                    </div>
                    <div className="blog-card-footer">
                      <span className="blog-card-author"><User size={12} className="blog-icon-1" />{` ${post.authorName}`}</span>
                      <span><Calendar size={12} className="blog-icon-1" />{` ${formatDate(post.publishedAt)}`}</span>
                    </div>
                  </Link>
                  );
                })}
              </div>
            )}

            {totalPages > 1 && (
              <div className="blog-pagination">
                <button className="btn btn-secondary" disabled={page === 0} onClick={() => setPage(p => p - 1)}>{t('common.previous2')}</button>
                <span className="blog-pagination-info">{`${t('common.page')} ${page + 1} ${t('common.of')} ${totalPages}`}</span>
                <button className="btn btn-secondary" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>{t('common.next2')}</button>
              </div>
            )}
          </>
        )}
        
        {/* Promotional CTA */}
        <div className="blog-cta-section blog-cta-section-spacing">
          <div className="blog-cta-content">
            <h2>{t('common.readyToAutomateYourReviewManagement')}</h2>
            <p>{t("common.joinThousandsOfLocalBusinessesUsing")}</p>
            <Link to="/signup" className="btn btn-primary btn-lg blog-cta-btn-spacing">{' ' + t('common.startYourFreeTrialToday')}</Link>
          </div>
        </div>
      </div>
    </BlogLayout>
  );
}
